import React, { useState } from 'react';
import { SidebarMenuItem, SidebarMenuButton } from 'src/shared/components/ui/sidebar';
import Modal from 'src/shared/components/modal/Modal';
import { SecondaryButton } from 'src/shared/components/button/Button';
import config from 'src/config';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import InfoMessage from 'src/shared/components/info-message/InfoMessage';
import authService from 'src/services/auth-service';
import {
    useGetCustomerAdminUsersQuery,
    useHasCustomerAdminUserQuery,
} from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import {
    MessageCircleQuestion,
    CircleHelp as HelpOutlineIcon,
    LifeBuoy as ContactSupportOutlined,
    CircleUser as AccountCircleOutlined,
} from 'lucide-react';
import useSupportAnalytics from 'src/global/hooks/useSupportAnalytics';
import useAuthService from 'src/shared/hooks/useAuthService';
import SupportMenuContactForm from 'src/global/components/sidebar/components/SupportMenuContactForm';

const SupportMenu = () => {
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
    const { isCurrentUserSuperAdmin } = useAuthService();

    const onClickHandler = () => {
        if (isCurrentUserSuperAdmin) {
            window.open(config.supportUrl);
        } else {
            setIsSupportModalOpen(true);
        }
    };

    return (
        <>
            <SidebarMenuItem>
                <SidebarMenuButton tooltip='Support' onClick={onClickHandler}>
                    <MessageCircleQuestion />
                    <span>Support</span>
                </SidebarMenuButton>
            </SidebarMenuItem>
            {isSupportModalOpen && (
                <Modal onClose={() => setIsSupportModalOpen(false)}>
                    <SupportModal />
                </Modal>
            )}
        </>
    );
};

const SupportModal = () => {
    const [shouldShowOrgAdminContactForm, setShouldShowOrgAdminContactForm] = useState(false);
    const { currentCustomerId } = useCustomer();
    const { trackSupportType } = useSupportAnalytics();

    const openNMSupport = () => {
        trackSupportType('NatureMetrics customer support');
        window.open(config.supportUrl);
    };

    const openAdminContactForm = () => {
        trackSupportType('My organisation`s administrator');
        setShouldShowOrgAdminContactForm(true);
    };

    const { currentData, isFetching } = useGetCustomerAdminUsersQuery(
        {
            customerId: currentCustomerId,
        },
        {
            skip: !currentCustomerId,
        }
    );

    const { currentData: hasCustomerAdminData, isFetching: isFetchingCustomerAdminPresence } = useHasCustomerAdminUserQuery(
        {
            customerId: currentCustomerId,
        },
        {
            skip: !currentCustomerId,
        }
    );
    const hasCustomerAdmin = hasCustomerAdminData?.hasCustomerAdmin || false;

    const { customerAdmins = [] } = currentData || {};

    const isCurrentUserCustomerAdmin = customerAdmins.some(admin => admin.cognitoId === authService.getCurrentUserData()?.cognitoId);

    if (shouldShowOrgAdminContactForm) {
        return <SupportMenuContactForm />;
    }

    const canContactOrgAdmin = Boolean(currentCustomerId) && !isCurrentUserCustomerAdmin && hasCustomerAdmin;

    return (
        <div className='p-6'>
            <div className='flex items-center gap-3 mb-6'>
                <HelpOutlineIcon />
                <div className='text-lg font-semibold'>Support</div>
            </div>
            <div className='mb-4'>
                <div className='text-sm font-medium mb-4'>I want to contact</div>
                <div className='flex flex-col gap-3'>
                    <SecondaryButton onClick={openNMSupport}>
                        NatureMetrics customer support <ContactSupportOutlined />
                    </SecondaryButton>
                    <SecondaryButton disabled={!canContactOrgAdmin || isFetching} onClick={openAdminContactForm}>
                        My organisation&apos;s administrator <AccountCircleOutlined />
                    </SecondaryButton>
                </div>
            </div>
            {!Boolean(currentCustomerId) && (
                <InfoMessage message="To contact your organisation's administrator for support, please select an organisation" />
            )}
            {isCurrentUserCustomerAdmin && (
                <InfoMessage message='You are the administor for this organisation. For support requests, please contact NatureMetrics customer support' />
            )}
            {!isFetchingCustomerAdminPresence && currentCustomerId && !hasCustomerAdmin && (
                <InfoMessage message='An administrator for your organisation has not been registered yet. For support requests, please contact NatureMetrics customer support' />
            )}
        </div>
    );
};

export default SupportMenu;
